'use client';
import { useImagePreviewContext } from '@ui/hooks/file/ImagePreviewContext';
import { IFileMeta } from '@ui/hooks/file/types';
import dynamic from 'next/dynamic';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface IProps {
  images: IFileMeta[];
}

export const MessageItemImages = ({ images }: IProps) => {
  const { setCarouselPreview } = useImagePreviewContext();

  const getWidthClass = (index: number) => {
    const length = images.length;
    if (length === 4) return index === 0 ? 'w-full max-h-[400px]' : 'w-1/3 max-h-[200px]';
    if (length === 3) return index === 0 ? 'w-full max-h-[400px]' : 'w-1/2 w-1/3 max-h-[200px]';
    if (length === 2) return 'w-1/2';
    return 'w-full max-h-[400px]';
  };

  const onClickImage = (event: React.MouseEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();

    const mediaItems = images.map((img) => ({
      src: img.source,
      isVideo: isVideo(img.mime_type as string),
      name: img.name,
    }));

    setCarouselPreview(mediaItems, index);
  };

  const isVideo = (mimeType?: string) => {
    return Boolean(mimeType?.startsWith('video/'));
  };

  return (
    <div className="flex flex-wrap">
      {images?.map((image, index) => {
        const isVideoFile = isVideo(image.mime_type as string);
        const isSingleMedia = images.length === 1;
        const isTwoMedia = images.length === 2;
        const isFirstInGrid = index === 0 && images.length > 2; // Only for 3+ items

        if (isVideoFile) {
          const isFourFilesGrid = images.length === 4 && index > 0;
          const isThreeFilesGrid = images.length === 3 && index > 0;

          return (
            <div
              key={index}
              className={`${getWidthClass(index)} p-1`}
              data-ignore-tap="true"
              onClick={(e) => onClickImage(e, index)}>
              <div
                className="relative rounded overflow-hidden bg-black h-full"
                style={{
                  ...(isSingleMedia && {
                    aspectRatio: '16/9',
                    maxHeight: '280px',
                    maxWidth: '500px',
                  }),
                  ...(isFirstInGrid && { aspectRatio: '16/9', maxHeight: '240px', width: '100%' }),
                  ...(isTwoMedia && { aspectRatio: '16/9', width: '100%', height: '100%' }),
                  ...(isFourFilesGrid && { width: '100%', height: '200px' }),
                  ...(isThreeFilesGrid && { width: '100%', height: '200px' }),
                }}>
                <ReactPlayer
                  url={image.source}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  config={{
                    file: {
                      attributes: {
                        controlsList: 'nodownload',
                        playsInline: true,
                      },
                    },
                  }}
                />
              </div>
            </div>
          );
        }

        return (
          <div
            key={index}
            className={`${getWidthClass(index)} ${images.length === 1 ? 'relative' : ''} p-1`}
            onClick={(e) => onClickImage(e, index)}
            data-ignore-tap="true">
            {images.length === 1 && (
              <div className="absolute inset-0 hidden max-h-[400px] w-full overflow-hidden sm:block">
                <img
                  src={image.source}
                  alt={image.name}
                  className=" h-full w-full object-cover blur-md"
                  loading="eager"
                />
              </div>
            )}
            <img
              src={image.source}
              alt={image.name}
              className={`h-full w-full cursor-pointer rounded ${
                images.length === 1 ? ' relative z-10 object-contain' : 'object-cover'
              }`}
              loading="eager"
            />
          </div>
        );
      })}
    </div>
  );
};
